import {TranslateService} from '@ngx-translate/core';
import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, tap } from 'rxjs';
import { TourResource } from '../resource/tour.resource';
import { MapTour, TourStep } from '../interfaces/map-tour';
import { CytoscapeService } from '../../services/cy-services/cytoscape.service';
import { ViewService } from './view.service';
import { MapService } from './map.service';
import { PackageService } from './package.service';
import { MODULE_NAMES } from '@maporium-workspace/shared';
import { invalidatePanZoomForCollapsedNode } from '../../../cytoscape-extensions/pan-zoom-storage';
import { isInIframe } from '../../shared/helpers/maporium.validators';
import { SavedView } from '../interfaces/saved-view';

@Injectable({providedIn: 'root'})
export class ToursService {
  private currentToursSignal = signal<MapTour[]>([]);
  private currentPublicToursSignal = signal<MapTour[]>([]);

  //@ts-ignore
  public activateTourSubject: BehaviorSubject<MapTour | null> = new BehaviorSubject(null);
  constructor(private translate: TranslateService,
              private resource: TourResource,
              private cyService: CytoscapeService,
              private mapService: MapService,
              private packageService: PackageService,
              private savedViewService: ViewService) {
  }

  get currentTours() {
    return this.currentToursSignal();
  }

  get currentPublicTours() {
    return this.currentPublicToursSignal();
  }

  public getAll(mapId: string) {
    return this.resource.getAll(mapId)
      .pipe(
        tap((v) => this.currentToursSignal.set(v))
      );
  }

  getAllPublic(mapId: string) {
    return this.resource.getAllPublic(mapId)
      .pipe(
        tap((v) => this.currentPublicToursSignal.set(v))
      );
  }

  public getOne(id: string) {
    return this.resource.getOne(id);
  }

  public save(mapTour: MapTour) {
    return this.resource.create(mapTour)
      .pipe(
        tap(() => CytoscapeService.menuChangedSubject.next())
      );
  }

  public update(mapTour: MapTour) {
    return this.resource.update(mapTour).pipe(
      tap(updated => {
        this.currentToursSignal.set(this.currentToursSignal().map(t => t.id === updated.id ? updated : t));
        this.currentPublicToursSignal.set(this.currentPublicToursSignal().map(t => t.id === updated.id ? updated : t));
        const active = this.activateTourSubject.value;
        if (active && active.id === updated.id) {
          this.activateTourSubject.next(updated);
        }
        CytoscapeService.menuChangedSubject.next();
      })
    );
  }

  public delete(mapTour: MapTour) {
    return this.resource.delete(mapTour.id as string).pipe(
      tap(() => {
        if (this.activateTourSubject.value?.id === mapTour.id) {
          this.activateTourSubject.next(null);
        }
        this.currentToursSignal.set(this.currentToursSignal().filter(t => t.id !== mapTour.id));
        this.currentPublicToursSignal.set(this.currentPublicToursSignal().filter(t => t.id !== mapTour.id));
        CytoscapeService.menuChangedSubject.next();
      })
    );
  }


  public playTour(mapTour: MapTour) {
    invalidatePanZoomForCollapsedNode();
    const cy = this.cyService.cy;
    if (!cy) {
      return;
    }

    this.savedViewService.activeScene$.next(null);
    this.activateTourSubject.next(mapTour);
  }

  public async getTourCtxMenuItems() {
    const mapId = this.mapService.getCurrentSelectedMapFromStore().id;
    if (!mapId) {
      return null;
    }

    const tours = isInIframe() ?
      await this.getAllPublic(mapId).toPromise() :
      await this.getAll(mapId).toPromise();

    if (!tours || tours.length === 0) {
      return null;
    }

    const subMenuWithTours = tours.map((tour) => {
      return {
        id: tour.id,
        content: tour.name,
        tooltipText: tour.description,
        onClickFunction: (event: any) => {
          this.playTour(tour);
        },
      };
    });

    return {
      id: 'tours_menu',
      content: this.translate.instant('CONTEXT_MENU.PLAY_STORY'),
      // tooltipText: 'Play Story',
      image: {src: 'assets/mat-symbols/outlined/play_circle.svg', width: 12, height: 12, x: 5, y: 6},
      coreAsWell: true,
      submenu: subMenuWithTours,
    };
  }

  private async animateStep(step: TourStep) {
    const cy = this.cyService.cy;
    if (!cy) {
      return;
    }

    const view = step.savedView;

    this.savedViewService.playView(view, step);
  }
}
